import * as React from "react";
import { useState } from "react";
import Grid from "@mui/material/Grid";

import AddWorklogCustomizedDialogs from "../../worklog/addWorklog/addWorklogDialog";
import { useCard } from "../../../../../utils/reactQuery/cards";
import { currentUserId, isAdmin } from "../../../../../utils/constants";
import CardLastSeenDetail from "./cardLastSeenDetail/CardLastSeenDetail";
import { useCustomSearchParams } from "../../../../../hooks/useCustomSearchParams";
import { useResponsive } from "../../../../../hooks/useResponsive";
import SubTask from "./components/subTask/SubTask";
import Collaborators from "./components/Collaborators";
import AllocatedHours from "./components/AllocatedHours";
import CardPriority from "./components/CardPriority";
import WatchlistButton from "./components/WatchlistButton";
import ReporterSection from "./components/ReporterSection";
import DateInfo from "./components/DateInfo";
import HoursSpent from "./components/HoursSpent";
import AssigneeSection from "./components/AssigneeSection";
import LabelSection from "./components/LabelSection";
import MilestoneSection from "./components/MilestoneSection";
import DueDateSection from "./components/DueDateSection";
import TimeTracking from "./components/TimeTracking";
import { useSidebarMutations } from "./hooks/useSidebarMutations";
import "./sidebar.scss";
import ResponsiveSidebar from "./ResponsiveSidebar";
import CustomFields from "./customFields";

export default React.memo(function SidebarGrid({ cardId, boardPermission }) {
  const [addWorklog, setAddWorklog] = useState(false);
  const [milestone, setMilestone] = useCustomSearchParams("milestone");
  const isMobile = useResponsive("down", "md");
  const {
    isLoading: isCardLoading,
    card,
    isError: isCardError,
  } = useCard(cardId);

  const data = React.useMemo(() => card?.data?.data?.responseData || [], [card]);

  const {
    board: {
      board_id: id,
      owner_user_id: boardOwnerId,
      key: boardKey,
      board_name: boardName,
    },
    collaborators,
    is_todo,
  } = card?.data?.data?.responseData;

  const checkBoardOwnerOrAdmin =
    isAdmin() || boardOwnerId === currentUserId() || boardPermission?.is_admin;

  const conditionCheck =
    checkBoardOwnerOrAdmin ||
    card?.data?.data?.responseData?.assigned_to_user_id === currentUserId() ||
    card?.data?.data?.responseData?.reporter === currentUserId();

  const {
    updateCardmutate,
    assignCardMutate,
    mutate,
    removeWatchListMutate,
    setDueDate,
    setShowEpic,
    dueDate,
    showEpic
  } = useSidebarMutations(cardId, data?.board_id, milestone);

  return (
    <>
      {addWorklog && (
        <AddWorklogCustomizedDialogs
          cardId={cardId}
          addWorklog={addWorklog}
          setAddWorklog={setAddWorklog}
          heading={"Add Work Log"}
          buttonName={"Save"}
        />
      )}
      <Grid container className={`sidebar-block ${isMobile ? "mt-2" : ""}`}>
        {!isMobile && (
          <Grid
            item
            className={isMobile ? "mobile-sidebar-heading" : "sidebar-heading"}
          >
            <CardLastSeenDetail cardDetail={data} />
          </Grid>
        )}

        <Grid item className="sidebar-details">
          <Grid item className={isMobile ? "" : "sidebar-details-content"}>
            {!isMobile && (
              <WatchlistButton
                inWatchlist={data?.in_watchlist}
                removeWatchListMutate={removeWatchListMutate}
                mutate={mutate}
                cardId={data?.card_id}
              />
            )}

            <ResponsiveSidebar
              heading={
                <WatchlistButton
                  inWatchlist={data?.in_watchlist}
                  removeWatchListMutate={removeWatchListMutate}
                  mutate={mutate}
                  cardId={data?.card_id}
                  isMobile={isMobile}
                />
              }
              isMobile={isMobile}
            >

              {data?.custom_fields
                ?.sort((a, b) => a.sequence - b.sequence)
                ?.map((field) => {

                  if (field?.display_name === "priority") {
                    return <CardPriority
                      priority={data?.priority}
                      cardId={data?.card_id}
                      updateCardmutate={updateCardmutate}
                      boardPermission={boardPermission}
                      checkBoardOwnerOrAdmin={checkBoardOwnerOrAdmin}
                    />
                  } else if (field?.display_name === "allocated_hours") {
                    return <AllocatedHours
                      data={data}
                      updateCardmutate={updateCardmutate}
                      checkBoardOwnerOrAdmin={checkBoardOwnerOrAdmin}
                      currentUserId={currentUserId}
                    />
                  } else if (field?.display_name === "time_tracking") {
                    return <TimeTracking
                      allocatedHours={data?.allocated_hours}
                      hoursSpent={data?.total_hours_spent}
                    />
                  } else if (field?.display_name === "hours_spent") {
                    return <HoursSpent hoursSpent={data?.total_hours_spent} />
                  } else if (field?.display_name === "label") {
                    return !data?.is_todo && boardPermission && (
                      <LabelSection
                        cardId={cardId}
                        data={data}
                        checkBoardOwnerOrAdmin={checkBoardOwnerOrAdmin ||  (boardPermission?.can_edit_task && data?.assigned_to_user_id == currentUserId() )}
                        currentUserId={currentUserId}
                      />
                    )
                  } else if (field?.display_name === "milestone") {
                    return <MilestoneSection
                      boardPermission={boardPermission}
                      showEpic={showEpic}
                      cardId={cardId}
                      setShowEpic={setShowEpic}
                      data={data}
                      checkBoardOwnerOrAdmin={checkBoardOwnerOrAdmin}
                      currentUserId={currentUserId}
                      updateCardmutate={updateCardmutate}
                    />
                  } else if (field?.display_name === "due_date") {
                    return <DueDateSection
                      open={dueDate}
                      updateCardmutate={updateCardmutate}
                      // data={data}
                      due_date={data?.due_date}
                      card_id={data?.card_id}
                      reporter={data?.reporter}
                      setDueDate={setDueDate}
                      checkBoardOwnerOrAdmin={checkBoardOwnerOrAdmin}
                      currentUserId={currentUserId}
                      boardPermission={boardPermission}
                      assigned_to_user_id={data?.assigned_to_user_id}
                    />
                  } else if (field?.display_name === "reporter") {
                    return <ReporterSection reporterData={data?.reported_by} />
                  } else if (field?.field_type === "custom") {
                    return <CustomFields field={field} boardPermission={boardPermission}/>
                  } else if (field?.display_name === "assignee") {
                    return <AssigneeSection
                      data={data}
                      conditionCheck={conditionCheck}
                      cardId={cardId}
                      assignCardMutate={assignCardMutate}
                      currentUserId={currentUserId}
                      boardPermission={boardPermission}
                    />
                  } else if (field?.display_name === "collaborators") {
                    return <Collaborators
                      ownerId={boardOwnerId}
                      boardId={id}
                      boardName={boardName}
                      cardCollabrator={collaborators}
                      assigneeUserId={data?.assigned_to_user_id}
                      canAddCollaborator={conditionCheck}
                      updateCardmutate={updateCardmutate}
                      cardId={cardId}
                    />
                  }
                })}

              {isMobile && (
                <div className="mobile-date-info">
                  <DateInfo
                    dateCreated={data?.date_created}
                    updatedAt={data?.updated_at}
                  />
                </div>
              )}
            </ResponsiveSidebar>

            {!is_todo && data && boardKey && (
              <SubTask
                cardDetail={data}
                boardKey={boardKey}
                boardPermission={boardPermission}
                isMobile={isMobile}
              />
            )}
          </Grid>
        </Grid>
        {!isMobile && (
          <DateInfo
            dateCreated={data?.date_created}
            updatedAt={data?.updated_at}
          />
        )}
      </Grid>
    </>
  );
})
